import { Drawer, DrawerProps } from 'antd';
import { useTranslate } from '@tolgee/react';
import * as React from 'react';
import AvatarPng from 'views/BootGuide/assets/avatar.png';
import { css } from '@emotion/css';
import CheckContainer from 'components/CheckContainer';
import { InputNumber, normalformatter } from './InputNumber';
import usePayPackage from '../hooks/usePayPackage';
import CurrencyCheckbox from './CurrencyCheckbox';
import PayTypeCheckbox from './PayTypeCheckbox';
import styled from '@emotion/styled';
import { CompanyAliHelp, Help } from './OrderAI/Help';
import { TrafficPkg } from './OrderAI/TrafficPkg';
import { useRequest } from 'ahooks';
import { CalculateTrafficPackagePriceParams, getTrafficPkgInfo } from 'services/api/pay';
import { useIsEnterprisePkg } from '../hooks/useComboInfo';
import { Formik } from 'formik';
import { useAuthorization } from 'components/Authorization';
import useSmartField from 'hooks/useSmartField';
import { OrderResult } from './OrderAI/OrderResult';
import { OrderAIFormikState } from './OrderAI/type';
import { WindupChildren, Pace } from 'windups';
import { PACEMS } from 'views/BootGuide/consts/paceMs';

export interface OrderAiServiceModalProps extends DrawerProps {
  trafficPkgInfo: Pay.TrafficPkg;
}

export const OrderAiServiceModal = React.memo((props: OrderAiServiceModalProps) => {
  const { visible, onClose, trafficPkgInfo, ...restProps } = props;
  const { t } = useTranslate();
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  if (!trafficPkgInfo) return null;
  return (
    <Drawer
      width={1216}
      footer={null}
      onClose={onClose}
      visible={visible}
      title={t('order_ai_buyer_count')}
      {...restProps}
      className={css`
        .ant-drawer-body {
          padding: 16px !important;
          margin: 0 !important;
        }
      `}
    >
      <Formik<OrderAIFormikState>
        initialValues={{
          orgId: payload?.orgId!,
          newPackageId: trafficPkgInfo.id as 8,
          handleNumCap: trafficPkgInfo.increaseTheMonthlyCap?.recommendHandleNum,
          handleNumTraffic: trafficPkgInfo.trafficPackage?.recommendTrafficPackageInfos.find(
            (each) => each.isSelected,
          )?.handleNum,
          trafficPackageType: undefined,
          currency: undefined,
          trafficPkgInfoOrigin: trafficPkgInfo,
        }}
        onSubmit={() => {}}
      >
        {() => {
          return <OrderAiService />;
        }}
      </Formik>
    </Drawer>
  );
});

OrderAiServiceModal.displayName = 'OrderAiServiceModal';

export const OrderAiService = React.memo(() => {
  const { t } = useTranslate();

  return (
    <OrderContainer>
      <WindupChildren>
        <div className="p-[16px]">
          <div className="leading-[22px] mb-[8px]">{t('aiorder_way')}</div>
          <AiServiceType />
        </div>
        <TypeRender />
      </WindupChildren>
    </OrderContainer>
  );
});

OrderAiService.displayName = 'OrderAiService';

const AiServiceType = React.memo(() => {
  const { t } = useTranslate();
  const { field, helpers } = useSmartField<Pay.TrafficPackageType>('trafficPackageType');

  return (
    <div className="flex gap-[16px]">
      <CheckContainer
        className="bg-[var(--fett-bg-panel)] !p-[16px]"
        checked={field.value === 'INCREASE_THE_MONTHLY_CAP'}
        onChange={() => {
          helpers.setValue('INCREASE_THE_MONTHLY_CAP');
        }}
      >
        <div className="leading-[20px] text-[var(--fett-text-primary)]">
          {t('aiorder_type1_title')}
        </div>
        <div className="text-[var(--fett-text-tertiary)] mt-[5px]">
          {t('aiorder_type1_subtitle')}
        </div>
      </CheckContainer>
      <CheckContainer
        className="bg-[var(--fett-bg-panel)] !p-[16px]"
        checked={field.value === 'TRAFFIC_PACKAGE'}
        onChange={() => {
          helpers.setValue('TRAFFIC_PACKAGE');
        }}
      >
        <div className="leading-[20px] text-[var(--fett-text-primary)]">
          {t('aiorder_type2_title')}
        </div>
        <div className="text-[var(--fett-text-tertiary)] mt-[5px]">
          {t('aiorder_type2_subtitle')}
        </div>
      </CheckContainer>
    </div>
  );
});

AiServiceType.displayName = 'AiServiceType';

const TypeRender = () => {
  const { field } =
    useSmartField<CalculateTrafficPackagePriceParams['trafficPackageType']>('trafficPackageType');
  const typeNode = !!field.value ? (
    field.value === 'INCREASE_THE_MONTHLY_CAP' ? (
      <Type1 />
    ) : (
      <Type2 />
    )
  ) : null;

  return typeNode ? (
    <>
      <div className="h-[1px] bg-[var(--fett-bg-app)]"></div>
      <div className="p-[16px]">{typeNode}</div>
    </>
  ) : null;
};

const Type1 = () => {
  const { t } = useTranslate();
  const isEnterprisePkg = useIsEnterprisePkg();
  const { field, helpers } = useSmartField<string>('handleNumCap');
  const { field: originField } =
    useSmartField<OrderAIFormikState['trafficPkgInfoOrigin']>('trafficPkgInfoOrigin');
  const { helpers: helpers2 } = useSmartField<string>('currency');

  const {
    currenciesOptions,
    availablePayTypes,
    currency,
    setCurrency,
    payType,
    setPayType,
    bank,
    setBank,
  } = usePayPackage();

  React.useEffect(() => {
    helpers2.setValue(currency);
  }, [currency, helpers2]);
  const [windupDone, setWindupDone] = React.useState(false);
  return (
    <Container>
      <WindupChildren onFinished={() => setWindupDone(true)}>
        <Pace ms={PACEMS}>
          <div>{t('aiorder_recommend_quota')}</div>
        </Pace>
      </WindupChildren>
      {windupDone && (
        <>
          <div className="mt-[12px] mb-[20px]">
            <h4>{t('aiorder_up_quota')}</h4>
            <InputNumber
              min={3000}
              max={1000000}
              step={1000}
              canInput
              value={field.value}
              onChange={(value) => {
                if (value) {
                  helpers.setValue(String(value));
                }
              }}
              stepInfoTemplate={t('aiorder_step_tip', { x: 1000 })}
              maxInfoTemplate={t('aiorder_max_tip', { x: normalformatter(1000000) })}
            />
          </div>
          <div className="mb-[20px]">
            <h4>{t('aiorder_order_month')}</h4>
            <div>
              {t('aiorder_num_month', { x: originField.value?.increaseTheMonthlyCap?.durationNum })}
            </div>
          </div>
          <div className="mb-[20px]">
            <CurrencyCheckbox
              currencies={currenciesOptions}
              value={currency}
              onChange={setCurrency}
            />
          </div>
          {isEnterprisePkg ? null : (
            <div>
              <PayTypeCheckbox
                payTypes={availablePayTypes}
                value={payType}
                onChange={setPayType}
                currency={currency}
                bank={bank}
                onBankChange={setBank}
              />
            </div>
          )}
          {currency === 'CNY' && (
            <div className="mt-[-12px]">
              <span className="mr-[6px]">
                {t('若需要开具公司抬头增值税发票，请使用公司支付宝进行支付')}
              </span>
              <CompanyAliHelp />
              <Help />
            </div>
          )}

          <OrderResult payType={payType} bank={bank} />
        </>
      )}
    </Container>
  );
};

const Type2 = () => {
  const { t } = useTranslate();
  const {
    // 是否企业套餐
    currenciesOptions,
    availablePayTypes,
    currency,
    setCurrency,
    payType,
    setPayType,
    bank,
    setBank,
  } = usePayPackage();
  const { helpers: helpers2 } = useSmartField<string>('currency');
  React.useEffect(() => {
    helpers2.setValue(currency);
  }, [currency, helpers2]);
  const [windupDone, setWindupDone] = React.useState(false);

  return (
    <Container>
      <WindupChildren onFinished={() => setWindupDone(true)}>
        <Pace ms={PACEMS}>
          <div>{t('aiorder_choose_pkg')}</div>
        </Pace>
      </WindupChildren>
      {windupDone && (
        <>
          <div className="mt-[12px] mb-[20px]">
            <TrafficPkg />
          </div>

          <div className="mb-[20px]">
            <CurrencyCheckbox
              currencies={currenciesOptions}
              value={currency}
              onChange={setCurrency}
            />
          </div>
          <div>
            <PayTypeCheckbox
              payTypes={availablePayTypes}
              value={payType}
              onChange={setPayType}
              currency={currency}
              bank={bank}
              onBankChange={setBank}
            />
          </div>
          {currency === 'CNY' && (
            <div className="mt-[-12px]">
              <span className="mr-[6px]">
                {t('若需要开具公司抬头增值税发票，请使用公司支付宝进行支付')}
              </span>
              <CompanyAliHelp />
              <Help />
            </div>
          )}

          <OrderResult payType={payType} bank={bank} />
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  h4 {
    font-weight: bold;
  }
  label {
    border-radius: 4px;
  }
`;

// const format = '0,0[.]00'

export const useGetTrafficPkgInfo = () => {
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { loading, data, runAsync } = useRequest(() => getTrafficPkgInfo(payload?.orgId!), {
    manual: true,
  });

  return {
    data,
    loading,
    runAsync,
  } as const;
};

const OrderContainer = (props: React.PropsWithChildren<{}>) => {
  const { children } = props;
  return (
    <div className="flex">
      <div className="w-[56px] h-[56px] mr-[12px]">
        <img className="h-full w-full" src={AvatarPng} alt="le" />
      </div>
      <div className="flex-grow bg-[var(--fett-erp-card-bg)] rounded-[4px]">{children}</div>
    </div>
  );
};
